import React, { useEffect, useState } from 'react'
import { gql } from 'graphql-request'
import { authorizedGraphQLRequest } from '@/api/graphqlClient'
import { Plus, Trash2, Calendar, Users, Clock, Coins } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { OrganisationWithDetails, Module, Subscription } from '../index'

interface SubscriptionsTabProps {
  organisation: OrganisationWithDetails | null
  modules: Module[]
  loading: boolean
  onRefresh: () => void
}

const CREATE_SUBSCRIPTION = gql`
  mutation createSubscription($input: CreateSubscriptionInput!) {
    createSubscription(input: $input) {
      id
      organisationId
      moduleId
      numUsers
      numTokens
      numSessions
      numMinutes
      dueDate
      active
    }
  }
`

const DELETE_SUBSCRIPTION = gql`
  mutation deleteSubscription($subscriptionId: Int!) {
    deleteSubscription(subscriptionId: $subscriptionId) {
      success
      message
    }
  }
`

// Earlier subscriptions of one module, with their users counted: what the form offers to copy from.
const GET_MODULE_SUBSCRIPTIONS = gql`
  query getModuleSubscriptions($organisationId: Int!, $moduleId: Int!) {
    getModuleSubscriptions(organisationId: $organisationId, moduleId: $moduleId) {
      id
      dueDate
      active
      numUsers
      userCount
    }
  }
`

type CopySource = Pick<Subscription, 'id' | 'dueDate' | 'active' | 'numUsers' | 'userCount'>

const SubscriptionsTab: React.FC<SubscriptionsTabProps> = ({ organisation, modules, loading, onRefresh }) => {
  const [showAddForm, setShowAddForm] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [formData, setFormData] = useState({
    moduleId: '',
    numUsers: '10',
    numTokens: '1000',
    numSessions: '100',
    numMinutes: '500',
    dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days from now
  })
  // A new subscription starts with no users; copying an earlier one's is opt-in.
  const [copySources, setCopySources] = useState<CopySource[]>([])
  const [copyFromId, setCopyFromId] = useState('')

  const organisationId = organisation?.id
  useEffect(() => {
    setCopySources([])
    setCopyFromId('')
    if (!showAddForm || !organisationId || !formData.moduleId) return

    let stale = false
    authorizedGraphQLRequest<{ getModuleSubscriptions: CopySource[] }>(GET_MODULE_SUBSCRIPTIONS, {
      organisationId,
      moduleId: parseInt(formData.moduleId),
    })
      .then((response) => {
        if (!stale) setCopySources((response.getModuleSubscriptions || []).filter((s) => (s.userCount ?? 0) > 0))
      })
      .catch((error) => console.error('Failed to fetch earlier subscriptions:', error))
    return () => {
      stale = true
    }
  }, [showAddForm, organisationId, formData.moduleId])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!organisation || !formData.moduleId) return

    try {
      setSubmitting(true)
      await authorizedGraphQLRequest(CREATE_SUBSCRIPTION, {
        input: {
          organisationId: organisation.id,
          moduleId: parseInt(formData.moduleId),
          numUsers: parseInt(formData.numUsers) || 0,
          numTokens: parseInt(formData.numTokens) || 0,
          numSessions: parseInt(formData.numSessions) || 0,
          numMinutes: parseInt(formData.numMinutes) || 0,
          dueDate: formData.dueDate,
          ...(copyFromId ? { copyUsersFromSubscriptionId: parseInt(copyFromId) } : {}),
        },
      })

      setShowAddForm(false)
      setFormData({
        moduleId: '',
        numUsers: '10',
        numTokens: '1000',
        numSessions: '100',
        numMinutes: '500',
        dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      })
      onRefresh()
    } catch (error: any) {
      console.error('Failed to create subscription:', error)
      // The server says why, e.g. more users to copy than seats.
      alert(error?.response?.errors?.[0]?.message || 'Failed to create subscription')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async (subscription: Subscription) => {
    const users = subscription.userCount ?? 0
    const question =
      users > 0
        ? `This subscription has ${users} ${users === 1 ? 'user' : 'users'} assigned. Deleting it removes them from the module. Delete it?`
        : 'Are you sure you want to delete this subscription?'
    if (!confirm(question)) return

    try {
      await authorizedGraphQLRequest(DELETE_SUBSCRIPTION, { subscriptionId: subscription.id })
      onRefresh()
    } catch (error) {
      console.error('Failed to delete subscription:', error)
      alert('Failed to delete subscription')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (!organisation) {
    return <div className="text-center text-gray-500 py-12">No organisation selected</div>
  }

  const allSubscriptions: Subscription[] = [...organisation.activeSubscriptions, ...(organisation.lastInactiveSubscription ? [organisation.lastInactiveSubscription] : [])]

  return (
    <div className="space-y-6">
      {/* Add Button */}
      <div className="flex justify-end">
        <Button onClick={() => setShowAddForm(!showAddForm)} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Add Subscription
        </Button>
      </div>

      {/* Add Form */}
      {showAddForm && (
        <form onSubmit={handleSubmit} className="bg-gray-50 rounded-xl p-6 space-y-4">
          <h4 className="font-semibold text-gray-800 mb-4">New Subscription</h4>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Module</label>
              <select
                value={formData.moduleId}
                onChange={(e) => setFormData({ ...formData, moduleId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                required
              >
                <option value="">Select a module</option>
                {modules.map((module) => (
                  <option key={module.id} value={module.id}>
                    {module.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
              <input
                type="date"
                value={formData.dueDate}
                onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Number of Users</label>
              <input
                type="number"
                value={formData.numUsers}
                onChange={(e) => setFormData({ ...formData, numUsers: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                min="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Number of Tokens</label>
              <input
                type="number"
                value={formData.numTokens}
                onChange={(e) => setFormData({ ...formData, numTokens: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                min="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Number of Sessions</label>
              <input
                type="number"
                value={formData.numSessions}
                onChange={(e) => setFormData({ ...formData, numSessions: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                min="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Number of Minutes</label>
              <input
                type="number"
                value={formData.numMinutes}
                onChange={(e) => setFormData({ ...formData, numMinutes: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                min="0"
              />
            </div>

            {copySources.length > 0 && (
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Copy users from</label>
                <select
                  value={copyFromId}
                  onChange={(e) => setCopyFromId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="">Don't copy (start with no users)</option>
                  {copySources.map((source) => (
                    <option key={source.id} value={source.id}>
                      {source.active ? 'Active' : 'Expired'} subscription, due {new Date(source.dueDate).toLocaleDateString()}: {source.userCount} {source.userCount === 1 ? 'user' : 'users'}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => setShowAddForm(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Creating...' : 'Create Subscription'}
            </Button>
          </div>
        </form>
      )}

      {/* Subscriptions List */}
      <div className="space-y-4">
        <h4 className="font-semibold text-gray-800">Active Subscriptions ({organisation.activeSubscriptions.length})</h4>

        {allSubscriptions.length === 0 ? (
          <div className="text-center text-gray-500 py-8 bg-gray-50 rounded-xl">
            <p>No subscriptions found</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {allSubscriptions.map((subscription) => (
              <div key={subscription.id} className={`bg-white border rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow ${subscription.active ? 'border-green-200' : 'border-red-200'}`}>
                <div className="flex justify-between items-start">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${subscription.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {subscription.active ? 'Active' : 'Expired'}
                      </span>
                      <span className="font-semibold text-gray-800">{subscription.module?.name || 'Unknown Module'}</span>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        <span>Due: {new Date(subscription.dueDate).toLocaleDateString()}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Users className="h-4 w-4" />
                        <span>
                          {subscription.userCount ?? 0} / {subscription.numUsers} users
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Coins className="h-4 w-4" />
                        <span>{subscription.numTokens} tokens</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        <span>{subscription.numMinutes} mins</span>
                      </div>
                    </div>
                  </div>

                  <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => handleDelete(subscription)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default SubscriptionsTab
